<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex items-center text-sm text-gray-500 inline-flex"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Deliverect Integration" />
            </div>

        </div>
        <div>
            <div class="mt-4 grid-cols-1 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Details
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Connect storekit to Deliverect.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-12">
                            <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                                <div class="sm:col-span-6">
                                    <base-input-field
                                        type="text"
                                        label="Deliverect ID"
                                        :disabled="true"
                                        tooltip="The deliverect channel link ID. Contact us to remove or change this value."
                                        id="deliverect-channel-id"
                                        v-model="settings.deliverectId"
                                    >
                                    </base-input-field>
                                </div>

                                <div class="sm:col-span-6">
                                    <toggle-switch
                                        id="sync-venue-hours"
                                        label="Use Deliverect opening hours"
                                        tooltip="Toggle to use the opening hours set for this venue in Deliverect."
                                        v-model="
                                            deliverect.syncVenueAvailabilityHours
                                        "
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <toggle-switch
                                        id="disable-busy-mode"
                                        label="Disable Busy Mode"
                                        tooltip="Disable busy mode functionality in Deliverect."
                                        v-model="deliverect.disableBusyMode"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <toggle-switch
                                        id="send-table-number-in-notes"
                                        label="Send Table Number in Notes"
                                        tooltip="Include table number information in order notes sent to Deliverect."
                                        v-model="deliverect.sendTableNumberInNotes"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <label class="block text-sm font-medium text-gray-700">
                                        Ready for Pickup Status Code
                                    </label>
                                    <input
                                        v-model.number="deliverect.readyForPickupStatusCode"
                                        class="form-input mt-1 block w-full sm:text-sm sm:leading-5"
                                        placeholder="eg 70"
                                        type="number"
                                        min="0"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <toggle-switch
                                        id="buffer-orders"
                                        label="Buffer Orders"
                                        tooltip="Orders are buffered in storekit and are only sent to Deliverect when they need to be prepared."
                                        v-model="deliverect.bufferOrders"
                                    />
                                </div>

                                <div class="sm:col-span-6" v-if="deliverect.bufferOrders">
                                    <label class="block text-sm font-medium text-gray-700">
                                        Buffer Delay (in minutes)
                                    </label>
                                    <input
                                        v-model.number="deliverect.bufferDelay"
                                        class="form-input mt-1 block w-full sm:text-sm sm:leading-5"
                                        placeholder="eg 30"
                                        type="number"
                                        min="0"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <toggle-switch
                                        id="send-cash-as-paid"
                                        label="Send Cash as Paid"
                                        tooltip="Mark cash orders as paid when sending to Deliverect."
                                        v-model="deliverect.sendCashAsPaid"
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
              <base-button :button-text="isLoading ? 'Saving...': 'Save'"
                           :loading="isLoading"
                           :disabled="isLoading"
                           class="ml-2"
                           button-style="action"
                           @clicked="updateSettings"
              />
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';
import BaseButton from "@/components/base/BaseButton.vue";

export default {
    name: 'DeliverectConnectedSettings',
    components: {BaseButton, ToggleSwitch },
    metaInfo: {
        title: 'Deliverect Integration'
    },
    props: ['venueId', 'venue'],
    data() {
        return {
            isLoading: false,
            settings: { ...this.venue },
            deliverect: {},
            defaultDeliverectSettings: {
                syncVenueAvailabilityHours: false,
                disableBusyMode: false,
                sendTableNumberInNotes: false,
                readyForPickupStatusCode: null,
                bufferOrders: false,
                bufferDelay: null,
                sendCashAsPaid: false
            }
        };
    },
    validations: {
        deliverect: {
            syncVenueAvailabilityHours: {
                required
            },
            disableBusyMode: {},
            sendTableNumberInNotes: {},
            readyForPickupStatusCode: {},
            bufferOrders: {},
            bufferDelay: {},
            sendCashAsPaid: {}
        }
    },
    mounted() {
        if (this.settings?.integrations?.deliverect) {
            this.deliverect = this.settings.integrations.deliverect;
        } else {
            this.deliverect = this.defaultDeliverectSettings;
        }
    },
    methods: {
        async updateSettings() {
            try {
              this.isLoading = true;
                await this.$axios.post('/deliverect/update', {
                    venueId: this.venueId,
                    ...this.deliverect
                });
                this.$notify({
                  group: 'settings',
                  title: 'Settings saved'
                });
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
              this.isLoading = false;
            }
        }
    }
};
</script>
